import React, { useState, useRef, useEffect } from 'react';
import { IFormField } from 'services/interface';
import "./MessageForm.scss";
import { imageLinks } from 'assets/images';

interface MessageFormProps {
    formField: IFormField;
    handleSendMessage: (value: string, action?: string, type?: string) => void,
    position?: boolean;
}

const InfoIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#fff" strokeWidth="2" />
        <path d="M12 16V12M12 8H12.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const feedbackButtons: any = {
    unsatisfied: {
        icon: imageLinks.icon.feedback_sad,
        label: 'Unsatisfied',
    },
    neutral: {
        icon: imageLinks.icon.feedback_neutral,
        label: 'Neutral',
    },
    satisfied: {
        icon: imageLinks.icon.feedback_happy,
        label: 'Satisfied!'
    },
};

const MessageForm: React.FC<MessageFormProps> = ({ formField, handleSendMessage, position }) => {
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [isInvalid, setIsInvalid] = useState(false);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

    const validateField = (value: string) => {
        if (!['phone', 'otp'].includes(formField.name)) {
            setIsInvalid(false);
            return;
        }

        if (formField.name === 'phone') {
            setIsInvalid(!/^\d{11}$/.test(value));
        } else if (formField.name === 'otp') {
            setIsInvalid(!/^\d{6}$/.test(value));
        }
    };


    const handleFeedbackChange = (value: any) => {
        setFormData({
            ...formData,
            [formField.name]: value
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        console.log("handleChange: ", e)
        const value = e.target.value;
        setFormData({
            ...formData,
            [formField.name]: value
        });

        // Clear existing timeout
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // Set new timeout for validation
        debounceTimeout.current = setTimeout(() => {
            validateField(value);
        }, 500); // 500ms debounce
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>, action?: string, actionLabel?: string) => {
        e.preventDefault();
        const isFormValueAction = action === "next" || action === "submit";
        const messageValue = isFormValueAction ? formData[formField.name] : actionLabel;
        handleSendMessage(messageValue || '', action, action === "submit" ? 'form-submit' : undefined);
        setFormData({ [formField.name]: '' }); // Set empty string for the specific field
    };



    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    return (
        <div>
            <form className="message-form" action="" onSubmit={handleSubmit}>
                <div className="input-wrapper" style={{ position: 'relative' }}>
                    {Boolean(formField.values?.length) && (
                        <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
                            {formField.values?.map((button: string, index: number) => {
                                return (
                                    <button onClick={(e) => {
                                        e.preventDefault();
                                        handleFeedbackChange(button)
                                    }}
                                        style={{
                                            cursor: 'pointer',
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: "50%",
                                            backgroundImage: `url(${feedbackButtons[button].icon})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            border: formData[formField.name] === button ? '2px solid #FFCB05' : 'none',
                                        }}
                                        key={feedbackButtons[button].icon + index}>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                    {formField.name !== "comment" && (
                        <input
                            name={formField.name}
                            value={formData[formField.name]}
                            onChange={handleChange}
                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                            type={formField.type}
                            placeholder={formField.placeholder}
                            style={{ display: formField.values && formField.values.length ? 'none' : 'block' }}
                        />
                    )}
                    {formField.name === "comment" && (
                        <textarea
                            name={formField.name}
                            value={formData[formField.name]}
                            onChange={handleChange}
                            className={'is-invalid'}
                            placeholder={formField.placeholder}
                            style={{ width: "90%", height: "50px", padding: "10px", border: "none", backgroundColor: "inherit" }}
                        />
                    )}
                    {isInvalid && (
                        <div style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer'
                        }}>
                            <InfoIcon />
                        </div>
                    )}
                </div>
                {
                    formField.actions && formField.actions.length ? <>
                        {
                            formField.actions.map(({ action, label }, index) => (
                                <button
                                    className={`btn btn__${index === 0 ? 'primary' : 'secondary'} btn__inline__form`}
                                    type="submit"
                                    key={index}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSubmit(new Event('submit') as unknown as React.FormEvent<HTMLFormElement>, action, label);
                                    }}>
                                    {label}
                                </button>
                            ))
                        }
                    </> :
                        <button className="btn btn__primary btn__inline__form" type="submit">SUBMIT</button>
                }
            </form>
        </div>
    );
};

export default MessageForm;
